// Vendor Billing v1.0 — compact per-loan VFI summary on the loan Vendors
// tab (replaces the v3.0 placeholder card). Chips first (five-second rule),
// then the open invoices, each deep-linking to the vendor's VFI ledger
// filtered to this loan. Self-gates on VendorBilling.Read so read-only loan
// roles without the billing grant see nothing broken.

import { Link } from "react-router-dom";
import { Card, StatusBadge } from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { formatCalendarDate } from "@/utils/datetime";
import {
  useVendorInvoices,
  useVfiScopedSummary,
} from "@/hooks/portfolioAdmin/useVendorBilling";
import type { VendorInvoiceDto } from "@/api/portfolioAdmin/types";
import { InvoiceStatusBadge } from "@/components/portfolioAdmin/vendors/billing/InvoiceStatusBadge";
import { fmtMoney } from "@/components/portfolioAdmin/vendors/billing/vendorBillingFormat";

export function LoanVfiSummaryCard({ loanId }: { loanId: string }) {
  const auth = useAuth();
  const canRead = auth.hasPermission("VendorBilling.Read");
  // D3 — vendor-audience only. A regulated vendor's borrower-billed fees are
  // Phoenix billing the BORROWER; they used to be listed and counted here.
  const invoicesQuery = useVendorInvoices(
    { loanId, audience: "vendor" },
    { enabled: canRead },
  );
  // Figures come from the server, against the same definitions the vendor
  // ledger tab and the registry dashboard use. Computing them here was how the
  // three screens ended up disagreeing about "open" and about Paid-YTD.
  const summaryQuery = useVfiScopedSummary({ loanId }, { enabled: canRead });

  if (!canRead) return null;

  const rows = invoicesQuery.data ?? [];
  const summary = summaryQuery.data;
  const open = rows.filter((r) => OPEN_STATUSES.includes(r.status));

  return (
    <Card data-testid="loan-vfi-summary-card">
      <h3 className="font-display font-bold text-h3 m-0 mb-space-2">
        VFI ledger (this loan)
      </h3>
      {invoicesQuery.isLoading && (
        <p className="text-small text-gray-mid italic m-0">Loading VFI summary…</p>
      )}
      {invoicesQuery.isError && (
        <p className="text-small text-status-error m-0">
          Could not load VFI invoices. Refresh to retry.
        </p>
      )}
      {invoicesQuery.isSuccess && (
        <div className="flex flex-col gap-space-3">
          <div className="flex flex-wrap items-center gap-space-2 text-small">
            <StatusBadge tone="neutral" data-testid="loan-vfi-open-chip">
              {summary?.openCount ?? 0} open · {fmtMoney(summary?.openTotal ?? 0)}
            </StatusBadge>
            {(summary?.overdueCount ?? 0) > 0 && (
              <StatusBadge tone="danger" data-testid="loan-vfi-overdue-chip">
                {summary!.overdueCount} overdue
              </StatusBadge>
            )}
            {/* Carved out of open, never dropped — money on its way. */}
            {(summary?.inTransitCount ?? 0) > 0 && (
              <StatusBadge tone="info" data-testid="loan-vfi-in-transit-chip">
                {summary!.inTransitCount} in transit · {fmtMoney(summary!.inTransitTotal)}
              </StatusBadge>
            )}
            <StatusBadge tone="ok">
              Paid YTD {fmtMoney(summary?.paidYtdTotal ?? 0)}
            </StatusBadge>
            {/* The vendor does not owe these — Phoenix bills the borrower —
                so they are labelled rather than merged into the chips above. */}
            {(summary?.borrowerBilledOpenCount ?? 0) > 0 && (
              <StatusBadge tone="muted" data-testid="loan-vfi-borrower-billed-chip">
                {summary!.borrowerBilledOpenCount} billed to borrower ·{" "}
                {fmtMoney(summary!.borrowerBilledOpenTotal)}
              </StatusBadge>
            )}
          </div>

          {rows.length === 0 ? (
            <p className="text-small text-gray-mid m-0">
              No VFI invoices on this loan yet. Issue one from a vendor
              assignment below or from the vendor&apos;s VFI Ledger tab.
            </p>
          ) : (
            <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
              {open.map((invoice) => (
                <OpenInvoiceRow key={invoice.id} invoice={invoice} loanId={loanId} />
              ))}
              {open.length === 0 && (
                <p className="text-small text-gray-mid m-0">
                  Nothing outstanding — all invoices on this loan are settled.
                </p>
              )}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

const OPEN_STATUSES: readonly string[] = ["issued", "viewed", "overdue"];

function OpenInvoiceRow({
  invoice,
  loanId,
}: {
  invoice: VendorInvoiceDto;
  loanId: string;
}) {
  return (
    <li className="flex items-center justify-between gap-space-2 border-b border-border-light pb-space-1 text-small">
      <span className="flex items-center gap-space-2 min-w-0">
        <Link
          to={`/portfolio-admin/vendors/${invoice.vendorId}?tab=vfi-ledger&loan=${loanId}&invoice=${invoice.id}`}
          className="text-gold-shadow hover:underline whitespace-nowrap"
          data-testid={`loan-vfi-invoice-link-${invoice.id}`}
        >
          {invoice.invoiceNumber}
        </Link>
        <span className="text-gray-mid truncate">{invoice.vendorName}</span>
        <InvoiceStatusBadge status={invoice.status} />
      </span>
      <span className="text-right whitespace-nowrap">
        <span className="tabular-nums">{fmtMoney(invoice.amount)}</span>
        {invoice.dueDate ? (
          <span className="text-caption text-gray-mid">
            {" "}· due {formatCalendarDate(invoice.dueDate)}
          </span>
        ) : null}
      </span>
    </li>
  );
}
